<script setup>
import {
  ref,
  computed,
  onMounted,
  onBeforeUnmount,
  onActivated,
  onDeactivated,
  watch,
} from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import conversationWorkflowsAPI from 'dashboard/api/conversationWorkflows';
import Button from 'dashboard/components-next/button/Button.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import { worldById } from 'dashboard/routes/dashboard/hub/hubWorldLookup';
import { workflowKindLabel } from './workflowKindLabels';

const route = useRoute();
const router = useRouter();
const { accountId } = useAccount();
const { t } = useI18n();

const workflowId = computed(() => route.params.workflowId);
// O backend aceita de 1 a 100 por página. Antes a tela mostrava só as 100
// execuções mais recentes e as antigas sumiam sem aviso.
const PER_PAGE = 50;
// A página vai na URL: sem isso o Voltar do detalhe (e o do navegador)
// remontava a lista na página 1.
const pageFromQuery = () => {
  const page = Number(route.query?.page);
  return Number.isInteger(page) && page > 1 ? page : 1;
};
const syncPageQuery = () => {
  const query = { ...route.query };
  if (currentPage.value > 1) query.page = String(currentPage.value);
  else delete query.page;
  router.replace({ query });
};
const executions = ref([]);
const currentPage = ref(pageFromQuery());
const totalCount = ref(0);
const isLoading = ref(true);
const loadError = ref(false);
const deletingId = ref(null);
const showDeleteConfirmationPopup = ref(false);
const selectedExecution = ref(null);
let requestToken = 0;
let deleteToken = 0;
const active = ref(true);
const mounted = ref(false);

const statusLabels = computed(() => ({
  running: t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STATUS.RUNNING'),
  waiting: t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STATUS.WAITING'),
  completed: t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STATUS.COMPLETED'),
  failed: t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STATUS.FAILED'),
  unconfirmed: t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STATUS.UNCONFIRMED'),
  delivery_attention: t(
    'CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STATUS.DELIVERY_ATTENTION'
  ),
}));

const statusLabel = status =>
  statusLabels.value[status] ||
  t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STATUS.UNKNOWN');

// Gatilho pelo nome da paleta e conversa pelo número que a equipe vê
// (#display_id), não pelo id interno.
const triggerLabel = kind => (kind ? workflowKindLabel(t, kind) : '—');
const conversationLabel = row =>
  row.conversation_display_id
    ? `#${row.conversation_display_id}`
    : row.conversation_id || '—';
const conversationRoute = row => ({
  name: 'inbox_conversation',
  params: {
    accountId: accountId.value,
    conversation_id: row.conversation_display_id,
  },
});

const formatTs = ts => {
  if (ts == null || ts === '') return '—';
  const n = Number(ts);
  if (!Number.isFinite(n)) return '—';
  return new Date(n * 1000).toLocaleString('pt-BR');
};

const statusClass = status => {
  const map = {
    running: 'bg-n-teal-3 text-n-teal-11',
    waiting: 'bg-n-amber-3 text-n-amber-11',
    completed: 'bg-n-teal-4 text-n-teal-12',
    failed: 'bg-n-ruby-3 text-n-ruby-11',
    unconfirmed: 'bg-n-amber-3 text-n-amber-11',
    delivery_attention: 'bg-n-amber-3 text-n-amber-11',
  };
  return map[status] || 'bg-n-slate-3 text-n-slate-11';
};

const load = async () => {
  requestToken += 1;
  const token = requestToken;
  const context = `${accountId.value}:${workflowId.value || ''}`;
  if (!workflowId.value) {
    executions.value = [];
    totalCount.value = 0;
    loadError.value = false;
    isLoading.value = false;
    return;
  }
  isLoading.value = true;
  loadError.value = false;
  try {
    const { data } = await conversationWorkflowsAPI.executions(
      workflowId.value,
      { page: currentPage.value, perPage: PER_PAGE }
    );
    if (
      token !== requestToken ||
      !active.value ||
      context !== `${accountId.value}:${workflowId.value || ''}`
    )
      return;
    const rows = data?.payload?.executions ?? [];
    const total = Number(data?.meta?.total_count);
    totalCount.value = Number.isFinite(total) ? total : rows.length;
    // A página ficou além do fim (ex.: apagou a última execução dela): vai
    // para a última página que ainda existe.
    const lastPage = Math.max(1, Math.ceil(totalCount.value / PER_PAGE));
    if (rows.length === 0 && currentPage.value > lastPage) {
      currentPage.value = lastPage;
      syncPageQuery();
      await load();
      return;
    }
    executions.value = rows;
  } catch (error) {
    if (
      token !== requestToken ||
      !active.value ||
      context !== `${accountId.value}:${workflowId.value || ''}`
    )
      return;
    loadError.value = true;
    if ([401, 403, 404].includes(error?.response?.status)) {
      executions.value = [];
      totalCount.value = 0;
    }
    useAlert(t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.LOAD_ERROR'));
  } finally {
    if (token === requestToken) isLoading.value = false;
  }
};

watch([accountId, workflowId], (next, previous) => {
  if (next[0] === previous?.[0] && next[1] === previous?.[1]) return;
  requestToken += 1;
  deleteToken += 1;
  executions.value = [];
  currentPage.value = 1;
  totalCount.value = 0;
  loadError.value = false;
  isLoading.value = true;
  deletingId.value = null;
  showDeleteConfirmationPopup.value = false;
  selectedExecution.value = null;
  if (active.value) load();
});
onMounted(() => {
  mounted.value = true;
  load();
});
onActivated(() => {
  if (!mounted.value || active.value) return;
  active.value = true;
  load();
});
onDeactivated(() => {
  active.value = false;
  requestToken += 1;
  deleteToken += 1;
  executions.value = [];
  loadError.value = false;
  isLoading.value = false;
  deletingId.value = null;
  showDeleteConfirmationPopup.value = false;
  selectedExecution.value = null;
});
onBeforeUnmount(() => {
  active.value = false;
  requestToken += 1;
  deleteToken += 1;
});

const showPagination = computed(
  () => executions.value.length > 0 && totalCount.value > PER_PAGE
);

const changePage = page => {
  if (page === currentPage.value) return;
  currentPage.value = page;
  syncPageQuery();
  load();
};

const goToEditor = () => {
  const target = {
    name: 'conversation_workflows_edit',
    params: {
      accountId: accountId.value,
      workflowId: workflowId.value,
    },
  };
  if (
    typeof route.query?.from_world === 'string' &&
    worldById(route.query.from_world)
  )
    target.query = { from_world: route.query.from_world };
  router.push(target);
};

const openExecution = row => {
  const target = {
    name: 'conversation_workflows_execution_detail',
    params: {
      accountId: accountId.value,
      workflowId: workflowId.value,
      executionId: String(row.id),
    },
  };
  if (
    typeof route.query?.from_world === 'string' &&
    worldById(route.query.from_world)
  )
    target.query = { from_world: route.query.from_world };
  if (currentPage.value > 1)
    target.query = { ...target.query, page: String(currentPage.value) };
  router.push(target);
};

const requestDelete = (row, event) => {
  event?.stopPropagation?.();
  selectedExecution.value = row;
  showDeleteConfirmationPopup.value = true;
};

const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
  selectedExecution.value = null;
};

const deleteExecution = async () => {
  const row = selectedExecution.value;
  if (!row) return;
  showDeleteConfirmationPopup.value = false;
  deleteToken += 1;
  const token = deleteToken;
  const context = `${accountId.value}:${workflowId.value || ''}`;
  selectedExecution.value = null;
  deletingId.value = row.id;
  try {
    await conversationWorkflowsAPI.executionDelete(workflowId.value, row.id);
    if (
      token !== deleteToken ||
      context !== `${accountId.value}:${workflowId.value || ''}`
    )
      return;
    useAlert(t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.DELETE_SUCCESS'));
    await load();
  } catch {
    if (
      token === deleteToken &&
      context === `${accountId.value}:${workflowId.value || ''}`
    )
      useAlert(t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.DELETE_ERROR'));
  } finally {
    if (token === deleteToken) deletingId.value = null;
  }
};
</script>

<template>
  <div
    data-testid="page-conversation-workflow-history"
    class="fixed inset-0 z-[100] flex flex-col bg-n-surface-1 text-n-slate-12 border-t border-n-weak"
  >
    <header
      class="flex flex-shrink-0 items-center gap-2 border-b border-n-weak bg-n-solid-1 px-3 py-2 sm:px-4"
    >
      <button
        type="button"
        class="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
        :title="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.BACK')"
        :aria-label="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.BACK')"
        @click="goToEditor"
      >
        <span class="i-ph-arrow-left size-5 shrink-0" aria-hidden="true" />
      </button>
      <h1 class="min-w-0 flex-1 truncate text-base font-medium text-n-slate-12">
        {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.TITLE') }}
      </h1>
      <Button
        slate
        outline
        size="sm"
        :label="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.REFRESH')"
        :is-loading="isLoading"
        @click="load"
      />
    </header>

    <div
      v-if="isLoading"
      class="flex flex-1 items-center justify-center text-n-slate-11"
    >
      {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.LOADING') }}
    </div>

    <div
      v-else-if="loadError && executions.length === 0"
      class="flex flex-1 flex-col items-center justify-center gap-3 px-4 text-center text-n-slate-11"
      role="alert"
    >
      <p class="m-0">
        {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.LOAD_ERROR') }}
      </p>
      <Button
        :label="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.RETRY')"
        slate
        outline
        size="sm"
        @click="load"
      />
    </div>

    <div
      v-else-if="executions.length === 0"
      class="flex flex-1 items-center justify-center px-4 text-center text-n-slate-11"
    >
      {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.EMPTY') }}
    </div>

    <div v-else class="min-h-0 flex-1 overflow-auto p-3 sm:p-4">
      <div
        v-if="loadError"
        class="mb-3 flex items-center justify-between gap-3 rounded-xl border border-n-ruby-6 bg-n-ruby-2 px-3 py-2 text-sm text-n-ruby-11"
        role="alert"
      >
        <span>{{
          $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.LOAD_ERROR')
        }}</span>
        <Button
          :label="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.RETRY')"
          slate
          outline
          size="sm"
          @click="load"
        />
      </div>
      <div
        class="hidden overflow-x-auto rounded-2xl border border-n-weak bg-n-solid-1 md:block"
      >
        <table class="w-full min-w-[56rem] border-collapse text-left text-sm">
          <thead>
            <tr
              class="border-b border-n-weak bg-n-alpha-2 text-xs font-medium uppercase tracking-wide text-n-slate-11"
            >
              <th class="px-3 py-3">
                {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.ID') }}
              </th>
              <th class="px-3 py-3">
                {{
                  $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STATUS_LABEL')
                }}
              </th>
              <th class="px-3 py-3">
                {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.TRIGGER') }}
              </th>
              <th class="px-3 py-3">
                {{
                  $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.CONVERSATION')
                }}
              </th>
              <th class="px-3 py-3">
                {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STEPS') }}
              </th>
              <th class="px-3 py-3">
                {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STARTED_AT') }}
              </th>
              <th class="px-3 py-3">
                {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.FINISHED_AT') }}
              </th>
              <th class="px-3 py-3">
                {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.ERROR') }}
              </th>
              <th class="w-px px-3 py-3 text-right">
                {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.ACTIONS') }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr
              v-for="row in executions"
              :key="row.id"
              class="cursor-pointer hover:bg-n-alpha-2/70"
              role="button"
              tabindex="0"
              @click="openExecution(row)"
              @keydown.enter="openExecution(row)"
            >
              <td class="px-3 py-2.5 font-mono text-n-slate-11">
                {{ row.id }}
              </td>
              <td class="px-3 py-2.5">
                <span
                  class="inline-flex rounded-md px-2 py-0.5 text-xs font-medium capitalize"
                  :class="statusClass(row.display_status || row.status)"
                >
                  {{ statusLabel(row.display_status || row.status) }}
                </span>
              </td>
              <td
                class="max-w-[12rem] truncate px-3 py-2.5 text-n-slate-12"
                :title="triggerLabel(row.trigger_kind)"
              >
                {{ triggerLabel(row.trigger_kind) }}
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">
                <router-link
                  v-if="row.conversation_display_id"
                  :to="conversationRoute(row)"
                  class="rounded font-medium text-n-blue-11 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-teal-9"
                  data-testid="execution-conversation-link"
                  @click.stop
                  @keydown.enter.stop
                >
                  {{ conversationLabel(row) }}
                </router-link>
                <span v-else class="font-mono">{{
                  conversationLabel(row)
                }}</span>
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">
                {{ row.steps_executed }}
              </td>
              <td class="whitespace-nowrap px-3 py-2.5 text-n-slate-11">
                {{ formatTs(row.started_at) }}
              </td>
              <td class="whitespace-nowrap px-3 py-2.5 text-n-slate-11">
                {{ formatTs(row.finished_at) }}
              </td>
              <td
                class="max-w-[14rem] truncate px-3 py-2.5 text-n-ruby-11"
                :title="row.error_message || ''"
              >
                {{ row.error_message || '—' }}
              </td>
              <td class="px-3 py-2.5 text-right">
                <button
                  type="button"
                  class="inline-flex h-8 w-8 items-center justify-center rounded-xl text-n-slate-11 transition-colors hover:bg-n-ruby-3 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-ruby-9 disabled:pointer-events-none disabled:opacity-40"
                  :title="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.DELETE')"
                  :aria-label="
                    $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.DELETE')
                  "
                  :disabled="deletingId !== null"
                  @click="requestDelete(row, $event)"
                >
                  <span
                    v-if="deletingId === row.id"
                    class="size-4 animate-spin rounded-full border-2 border-n-slate-8 border-t-transparent"
                    aria-hidden="true"
                  />
                  <svg
                    v-else
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="size-[18px] shrink-0"
                    aria-hidden="true"
                  >
                    <path d="M3 6h18" />
                    <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                    <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                    <line x1="10" x2="10" y1="11" y2="17" />
                    <line x1="14" x2="14" y1="11" y2="17" />
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="space-y-3 md:hidden">
        <article
          v-for="row in executions"
          :key="`mobile-${row.id}`"
          class="rounded-2xl border border-n-weak bg-n-solid-1 p-3"
        >
          <div class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <p class="m-0 text-xs text-n-slate-11">
                {{
                  $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.EXECUTION', {
                    id: row.id,
                  })
                }}
              </p>
              <span
                class="mt-1 inline-flex rounded-md px-2 py-0.5 text-xs font-medium"
                :class="statusClass(row.display_status || row.status)"
                >{{ statusLabel(row.display_status || row.status) }}</span
              >
            </div>
            <button
              type="button"
              class="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-xl text-n-slate-11 hover:bg-n-ruby-3 hover:text-n-ruby-11"
              :title="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.DELETE')"
              :aria-label="
                $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.DELETE')
              "
              :disabled="deletingId !== null"
              @click="requestDelete(row, $event)"
            >
              <span
                v-if="deletingId === row.id"
                class="size-4 animate-spin rounded-full border-2 border-n-slate-8 border-t-transparent"
                aria-hidden="true"
              />
              <span v-else class="i-ph-trash size-5" aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            class="mt-3 w-full rounded-xl border border-n-weak p-3 text-left hover:bg-n-alpha-2"
            @click="openExecution(row)"
          >
            <dl class="grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
              <div>
                <dt class="text-xs text-n-slate-11">
                  {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.TRIGGER') }}
                </dt>
                <dd class="m-0 truncate text-n-slate-12">
                  {{ triggerLabel(row.trigger_kind) }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-n-slate-11">
                  {{
                    $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.CONVERSATION')
                  }}
                </dt>
                <dd class="m-0 text-n-slate-12">
                  {{ conversationLabel(row) }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-n-slate-11">
                  {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STEPS') }}
                </dt>
                <dd class="m-0 text-n-slate-12">
                  {{ row.steps_executed ?? '—' }}
                </dd>
              </div>
              <div>
                <dt class="text-xs text-n-slate-11">
                  {{
                    $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.STARTED_AT')
                  }}
                </dt>
                <dd class="m-0 text-n-slate-12">
                  {{ formatTs(row.started_at) }}
                </dd>
              </div>
              <div class="col-span-2">
                <dt class="text-xs text-n-slate-11">
                  {{ $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.ERROR') }}
                </dt>
                <dd class="m-0 break-words text-n-ruby-11">
                  {{ row.error_message || '—' }}
                </dd>
              </div>
            </dl>
          </button>
        </article>
      </div>
    </div>
    <div v-if="showPagination" class="relative flex-shrink-0">
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="PER_PAGE"
        @update:current-page="changePage"
      />
    </div>
    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="deleteExecution"
      :title="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.DELETE')"
      :message="
        $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.DELETE_CONFIRM', {
          id: selectedExecution?.id || '',
        })
      "
      :confirm-text="
        $t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.CONFIRM_DELETE')
      "
      :reject-text="$t('CONVERSATION_WORKFLOW.EXECUTIONS_HISTORY.CANCEL')"
    />
  </div>
</template>
